import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Festivo } from '../../types/index';
import { useCreateMultipleFestivosMutation, useDeleteFestivoMutation  } from '../../services/hooks';
import { fetchFestivos, fetchEventosByDateRangeRaw } from '../../services/api';
import { useQuery } from '@tanstack/react-query';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ConfirmationModal from '../common/ConfirmationModal';
import { toast } from 'react-toastify';

interface FestivosModalProps {
    onClose: () => void;
}

// Festivos fijos
const festivosFijos = [
    { dia: 1, mes: 1, descripcion: "Año Nuevo" },
    { dia: 6, mes: 1, descripcion: "Día de Reyes" },
    { dia: 1, mes: 5, descripcion: "Día del Trabajador" },
    { dia: 12, mes: 10, descripcion: "Fiesta Nacional de España" },
    { dia: 1, mes: 11, descripcion: "Día de Todos los Santos" },
    { dia: 6, mes: 12, descripcion: "Día de la Constitución" },
    { dia: 8, mes: 12, descripcion: "Día de la Inmaculada Concepción" },
    { dia: 25, mes: 12, descripcion: "Navidad" }
];

const FestivosModal: React.FC<FestivosModalProps> = ({ onClose }) => {
    const [selectedDates, setSelectedDates] = useState<Date[]>([]);
    const [descriptions, setDescriptions] = useState<string[]>([]);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState('');
    const [selectedFestivoId, setSelectedFestivoId] = useState('');
    // P3 — qué se confirma: borrar un festivo (null, el caso de siempre) o crear
    // unos festivos que van a cancelar clases ya existentes.
    const [accionPendiente, setAccionPendiente] = useState<(() => void) | null>(null);
    const [comprobandoImpacto, setComprobandoImpacto] = useState(false);

     // Query para obtener festivos
    const { data: festivos, isLoading } = useQuery<Festivo[]>({
        queryKey: ['festivos'],
        queryFn: fetchFestivos,
    });


    // Función auxiliar para verificar si una fecha ya existe como festivo
    const isFestivoExisting = (date: Date, existingFestivos: Festivo[]): boolean => {
        return existingFestivos.some(existingFestivo => {
            const existingDate = new Date(existingFestivo.fecha);
            return (
                existingDate.getDate() === date.getDate() &&
                existingDate.getMonth() === date.getMonth() &&
                existingDate.getFullYear() === date.getFullYear()
            );
        });
    };

    //llamando a la mutación para modificar un evento
    const {mutateAsync: createMultipleFestivos } = useCreateMultipleFestivosMutation();

    // Clave 'YYYY-MM-DD' del día natural, sin pasar por UTC. El resto del fichero
    // normaliza a mano con getTimezoneOffset(); aquí no, porque una fecha cerca de
    // medianoche se iría de día y el aviso contaría las clases del día de al lado.
    const ymd = (date: Date) => [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
    ].join('-');

    /**
     * P3 — qué clases se van a cancelar al marcar estos días como festivos.
     *
     * Marcar un festivo cancela las clases que ya existían ese día y compensa a
     * los clientes inscritos, así que no puede pasar sin avisar. Se consulta con
     * el endpoint de rango que ya existe, en vez de añadir uno de impacto.
     *
     * Es solo una PREVISUALIZACIÓN: la decisión la toma el backend al guardar, y
     * los números que luego se enseñan son los que él devuelve.
     */
    const contarImpacto = async (dates: Date[]) => {
        if (!dates.length) return { clases: 0, clientes: 0 };

        const ordenadas = [...dates].sort((a, b) => a.getTime() - b.getTime());
        const dias = new Set(dates.map(ymd));
        const ahora = new Date();

        const eventos = await fetchEventosByDateRangeRaw(
            ymd(ordenadas[0]),
            ymd(ordenadas[ordenadas.length - 1]),
        );

        // Mismo criterio que el backend: solo las que no han empezado. Marcar como
        // festivo un día pasado no cancela clases que ya se dieron.
        const afectados = eventos.filter((e) => {
            if (e.cancelado) return false;
            const fechaDia = String(e.fecha).split('T')[0];
            if (!dias.has(fechaDia)) return false;
            return new Date(`${fechaDia}T${e.horaInicio}:00`) > ahora;
        });

        return {
            clases: afectados.length,
            clientes: afectados.reduce((total, e) => total + (e.clientesInscritos?.length ?? 0), 0),
        };
    };

    /**
     * Pide confirmación si hay clases por medio, y si no guarda directamente.
     */
    const guardarConAviso = async (dates: Date[], guardar: () => void) => {
        setComprobandoImpacto(true);
        try {
            const { clases, clientes } = await contarImpacto(dates);
            if (clases === 0) {
                guardar();
                return;
            }
            const txtClases = clases === 1 ? '1 clase ya programada' : `${clases} clases ya programadas`;
            const txtClientes = clientes === 1
                ? 'y se dará 1 recuperación'
                : `y se darán ${clientes} recuperaciones`;
            setConfirmationMessage(
                `Se cancelará ${txtClases} ${txtClientes}. Los clientes de prueba no reciben recuperación. ¿Continuar?`
            );
            setAccionPendiente(() => guardar);
            setShowConfirmationModal(true);
        } catch {
            // Si no se puede previsualizar, no se bloquea la operación: el backend
            // decide igual. Pero se dice, para que nadie lea el silencio como "no
            // hay clases ese día".
            toast.warning('No se ha podido comprobar si hay clases esos días. Se guardará igualmente.');
            guardar();
        } finally {
            setComprobandoImpacto(false);
        }
    };

    //llamando a la mutación para eliminar un evento
    const {mutateAsync: deleteFestivo } = useDeleteFestivoMutation();

    const handleDateChange = (date: Date) => {
        if (!selectedDates.some(selectedDate => selectedDate.getTime() === date.getTime())) {
            setSelectedDates([...selectedDates, date]);
            setDescriptions([...descriptions, '']);
        }
    };

    const handleDescriptionChange = (index: number, description: string) => {
        const newDescriptions = [...descriptions];
        newDescriptions[index] = description;
        setDescriptions(newDescriptions);
    };

    // Función para guardar los festivos seleccionados
    const handleSave = () => {
        if (!festivos) return;

        // Filtramos las fechas que no existen como festivos
        const validDates = selectedDates.filter(date => !isFestivoExisting(date, festivos));
    
        if (validDates.length === 0) {
            toast.info('Todas las fechas seleccionadas ya están registradas como festivos');
            return;
        }
    
        // Si algunas fechas ya existen, notificamos al usuario
        if (validDates.length < selectedDates.length) {
            toast.warning('Algunas fechas seleccionadas ya existían y no se duplicarán');
        }
    
        const newFestivos: Festivo[] = validDates.map((date, index) => {
            // Ajustar la fecha a la zona horaria local antes de guardarla
            const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString();
            return {
                fecha: localDate,
                descripcion: descriptions[selectedDates.indexOf(date)],
            };
        });

        void guardarConAviso(validDates, () => {
            createMultipleFestivos(newFestivos);
            setSelectedDates([]);
            setDescriptions([]);
        });
    };

    // Función para eliminar una fecha seleccionada
    const handleRemoveDate = (index: number) => {
        const newDates = [...selectedDates];
        const newDescriptions = [...descriptions];
        newDates.splice(index, 1);
        newDescriptions.splice(index, 1);
        setSelectedDates(newDates);
        setDescriptions(newDescriptions);
    };

    // Función para mostrar el modal de confirmación antes de eliminarlo de base de datos
    const handleRemoveFestivo = (festivoId: string) => {
        setSelectedFestivoId(festivoId);
        // P3 — se dice lo que NO hace: quitar el festivo no devuelve las clases que
        // se cancelaron al marcarlo. Se decidió no reestablecerlas en automático
        // (el horario puede haberse regenerado y las recuperaciones ya gastadas),
        // así que al menos no se deja creer lo contrario.
        setConfirmationMessage(
            '¿Estás seguro de eliminar este festivo? Las clases que se cancelaron al marcarlo no se reestablecen.'
        );
        setAccionPendiente(null);
        setShowConfirmationModal(true);
    };

    // Función para eliminar un festivo de la base de datos
    const deleteFestivoHandler = () => {
        deleteFestivo(selectedFestivoId);
        setShowConfirmationModal(false);
    }

    
    //Función para guardar los festivos fijos de forma masiva
    const handleSaveFestivosFijos = () => {
        if (!festivos) return;

        const currentYear = new Date().getFullYear();
        
        // Filtramos los festivos que no existen
        const festivosToAdd = festivosFijos.filter(festivoFijo => {
            const festivoDate = new Date(currentYear, festivoFijo.mes - 1, festivoFijo.dia);
            return !isFestivoExisting(festivoDate, festivos);
        });

        if (festivosToAdd.length === 0) {
            toast.info('Todos los festivos fijos ya están registrados para este año');
            return;
        }

        // Creamos los nuevos festivos
        const nuevosFestivos = festivosToAdd.map(festivo => {
            const fecha = new Date(currentYear, festivo.mes - 1, festivo.dia);
            const localDate = new Date(fecha.getTime() - (fecha.getTimezoneOffset() * 60000)).toISOString();

            return {
                fecha: localDate,
                descripcion: festivo.descripcion,
            };
        });

        const fechasFijas = festivosToAdd.map(
            (festivo) => new Date(currentYear, festivo.mes - 1, festivo.dia)
        );
        void guardarConAviso(fechasFijas, () => createMultipleFestivos(nuevosFestivos));
    };
    

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="relative bg-white rounded-lg shadow-lg p-6 w-full max-w-xl max-h-[80vh] overflow-y-auto">
                <button 
                    onClick={onClose} 
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                >
                    <X size={24} />
                </button>
                <h2 className="text-xl font-bold mb-4 text-center">Agregar Festivos</h2>
                <div className='flex justify-center'>
                    <div className="mb-2 w-1/2">
                        <label className="block text-lg font-bold mb-2">Seleccionar Fechas:</label>
                        <DatePicker
                            selected={null}
                            onChange={(date: Date | null) => date && handleDateChange(date)}
                            inline
                            highlightDates={selectedDates}
                        />
                    </div>
                    <div className='mb-2 w-1/2 text-center'>
                        <label className="block text-lg font-bold mb-2">Festivos Fijos:</label>
                        <div className='mt-1'>
                            {festivosFijos.map((festivo, index) => (
                                <div key={index} className="mb-1">
                                    <div className="flex justify-start gap-2 items-center">
                                        <div className='w-[20%] text-right'>
                                            <label className="block text-gray-700 text-sm">{festivo.dia}/{festivo.mes}:</label>
                                        </div>
                                        <div className='w-[80%] text-sm text-left'>
                                            <p>{festivo.descripcion}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={handleSaveFestivosFijos}
                            disabled={comprobandoImpacto}
                            className={`px-4 py-2 ${comprobandoImpacto ? 'bg-gray-500' : 'bg-blue-500'} text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
                        >
                            {comprobandoImpacto ? 'Comprobando clases...' : 'Guardar Festivos Fijos'}
                        </button>
                    </div>
                </div>
                
                <div className='flex gap-4'>
                    {festivos && festivos.length > 0 && (
                        <div className='w-1/2 text-center'>
                            <h3 className="text-lg font-bold mb-2">Festivos Registrados:</h3>
                            {festivos.map((festivo) => (
                                <div key={festivo._id} className="mb-2">
                                    <div className="flex justify-start gap-6 items-center">
                                        <div className='w-[20%] text-right'>
                                            <label className="block text-gray-700 text-sm">{new Date(festivo.fecha).toLocaleDateString()}:</label>
                                        </div>
                                        <div className='w-[60%] text-sm'>
                                            <p>{festivo.descripcion}</p>
                                        </div>
                                        <div className='w-[20%] text-sm'>
                                        <button
                                            type="button"
                                            onClick={() => festivo._id && handleRemoveFestivo(festivo._id)}
                                            className="bg-red-500 text-white rounded-full p-1 "
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {selectedDates.length > 0 && (
                    <div className='w-1/2 text-center'>
                        <h3 className="text-xl font-bold mb-2">Fechas Seleccionadas:</h3>
                    
                        {selectedDates.map((date, index) => (
                            <div key={index} className="mb-2">
                                <div className="flex justify-start gap-4 items-center">
                                    <div className='w-[20%] text-right text-sm'>
                                        <label className="block text-gray-700">{date.toLocaleDateString()}:</label>
                                    </div>
                                    <div className='w-[60%] text-sm'>
                                        <input
                                            type="text"
                                            value={descriptions[index]}
                                            onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                            className="mt-1 p-2 border rounded w-full"
                                            placeholder='Descripción'
                                        />
                                    </div>
                                    <div className='w-[20%] text-sm'>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveDate(index)}
                                            className="bg-red-500 text-white rounded-full p-1 "
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                    
                                </div>
                                
                            </div>
                        ))}
                    </div>
                    )}
                    
                </div>
                <div className="flex justify-end mt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="mr-2 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={selectedDates.length === 0 || comprobandoImpacto}
                        className={`px-4 py-2 ${selectedDates.length === 0 || comprobandoImpacto ? 'bg-gray-500': 'bg-blue-500'}  text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                        {comprobandoImpacto ? 'Comprobando clases...' : 'Guardar'}
                    </button>
                </div>
            </div>
            {showConfirmationModal && (
                <ConfirmationModal
                    isOpen={showConfirmationModal}
                    confirmMessage={confirmationMessage}
                    onClose={() => { setShowConfirmationModal(false); setAccionPendiente(null); }}
                    // P3 — el modal servía solo para borrar un festivo. Ahora hay
                    // dos acciones que confirmar, así que la que esté en curso se
                    // guarda en `accionPendiente`; sin ella, confirmar el aviso de
                    // clases canceladas habría borrado un festivo.
                    onConfirm={() => {
                        const accion = accionPendiente;
                        setShowConfirmationModal(false);
                        setAccionPendiente(null);
                        if (accion) accion(); else deleteFestivoHandler();
                    }}
                />
            )}
        </div>
    );
};

export default FestivosModal;